import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IonContent, IonMenu, IonMenuToggle } from '@ionic/react';
import { LogIn } from 'lucide-react';
import { useAuthStore } from '../../store/useAuthStore';
import { SUPPORTED_LANGUAGES } from '../../i18n/config';
import { LanguageSwitcher } from '../profile/LanguageSwitcher';
import { AppearanceControl } from './AppearanceControl';
import { SignInModal } from '../auth/SignInModal';

// Ionic menu id — TopBar's hamburger opens this via
// `menuController.open(GUEST_MENU_ID)`. Single source of truth shared between
// the menu and its trigger (mirrors PROFILE_MENU_ID).
export const GUEST_MENU_ID = 'guest-menu';

/**
 * Right-side navigation drawer for logged-out users, opened from the TopBar
 * hamburger. The unauth counterpart to ProfileMenu: a stack of cards giving a
 * browsing guest the Sign In CTA, the language switcher, and the appearance/
 * theme control — all usable before they have an account. Rendered once at the
 * app root and addressed by GUEST_MENU_ID.
 *
 * Self-hides when authenticated — ProfileMenu owns the same #main-content
 * `side="end"` slot for signed-in users, and only one of the two is ever
 * mounted at a time, so the two end-side menus never collide.
 *
 * i18n note: this component mounts OUTSIDE the page-level Suspense boundary
 * (it's a sibling of #main-content), so it must only use bootstrapped
 * namespaces — `common` here. Pulling in a lazy namespace (e.g. `profile`)
 * would bubble its load-suspend to the root and flash the whole app; that's
 * why the appearance strings live in `common`, not `profile`.
 */
export const GuestMenu: React.FC = () => {
  const { t } = useTranslation('common');
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [signInOpen, setSignInOpen] = useState(false);

  if (isAuthenticated) return null;

  return (
    <>
      <IonMenu menuId={GUEST_MENU_ID} contentId="main-content" side="end">
        <IonContent>
          <div className="flex h-full flex-col gap-3 p-4">
            {/* Sign In card — the primary CTA. Title + one-line pitch + button.
                IonMenuToggle closes the drawer on tap; the modal then opens
                over the (now-closed) menu. */}
            <div className="flex flex-col gap-3 rounded-xl border border-border bg-card text-card-foreground p-4">
              <div className="space-y-1">
                <h2 className="text-base font-semibold">{t('guestMenu.signInCard.title')}</h2>
                <p className="text-sm text-muted-foreground">{t('guestMenu.signInCard.body')}</p>
              </div>
              <IonMenuToggle>
                <button
                  type="button"
                  data-testid="guest-menu-sign-in"
                  onClick={() => setSignInOpen(true)}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-primary px-4 py-3 text-base font-semibold text-white active:opacity-80"
                >
                  <LogIn className="h-5 w-5 shrink-0" aria-hidden="true" />
                  <span className="min-w-0 text-center leading-tight">
                    {t('guestMenu.signInCard.cta')}
                  </span>
                </button>
              </IonMenuToggle>
            </div>

            {/* Language card — only when there's a real choice to make. Guarded
                on SUPPORTED_LANGUAGES so a single-language config never renders
                an empty titled card (the switcher itself also self-hides). */}
            {SUPPORTED_LANGUAGES.length >= 2 && (
              <div className="rounded-xl border border-border bg-card text-card-foreground p-4 space-y-3">
                <h2 className="text-base font-semibold">{t('guestMenu.languageCard.title')}</h2>
                <LanguageSwitcher />
              </div>
            )}

            {/* Appearance card — same light/system/dark control as Settings,
                bound to the shared useThemeStore (persists to localStorage, no
                auth needed). */}
            <div className="rounded-xl border border-border bg-card text-card-foreground p-4 space-y-3">
              <h2 className="text-base font-semibold">{t('appearance.title')}</h2>
              <AppearanceControl />
            </div>
          </div>
        </IonContent>
      </IonMenu>
      <SignInModal isOpen={signInOpen} onClose={() => setSignInOpen(false)} />
    </>
  );
};
